import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Modal } from "reactstrap";
import { useAuth } from "../authContext";
import { useCart } from "../cartContext";
import { useNavigate } from "react-router-dom";
import axios from "../lib/axios";
import {
  formatTierName,
  formatMediaType,
  formatAudience,
  formatFieldValue,
} from "../utils/formatters";
import RegisterModal from "../Modals/RegisterModal";
import { toast } from "react-toastify";
import {
  FaFilm,
  FaBus,
  FaBullhorn,
  FaMinus,
  FaPlus,
  FaUsers,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaTwitter,
  FaRadio,
  FaCartShopping,
} from "react-icons/fa6";
import {
  FaMapMarkedAlt,
  FaRegCopy,
  FaLanguage,
  FaLayerGroup,
  FaDownload,
  FaLock,
} from "react-icons/fa";
import { SiYoutubemusic } from "react-icons/si";
import { LuX } from "react-icons/lu";
import BrandEndorsementQuotationModal from "./BrandEndorsementQuotationModal";
import { motion } from "framer-motion";
import YoutubeModal from "./YoutubeModal";
import { getYoutubeVideoId } from "../utils/youtube";
import {
  getDesignCatalogPath,
  isDesignCatalogCategory,
} from "../utils/designing";
import ConfettiBurst, { createConfettiPieces } from "./ConfettiBurst";
import { AudioPlayer } from "./card/AudioPlayer";
import { DesignOptionDropdown } from "./card/DesignOptionDropdown";
import { PriceWithUnit } from "./card/PriceWithUnit";
import PrintingCart from "./card/PrintingCart";
import LiveDesignerBooking from "./LiveDesignerBooking";

const cartModalCutoutStyle = {
  backgroundImage:
    "radial-gradient(circle at 12px 12px, #ffffff 0 12px, transparent 12.5px)",
  backgroundPosition: "left top",
  backgroundRepeat: "repeat-x",
  backgroundSize: "38px 24px",
  height: "24px",
};

const formatRupees = (value) => Number(value || 0).toLocaleString();

const PriceComparison = ({
  currentPrice,
  originalPrice,
  unit,
  className = "justify-end",
  highlightClassName = "text-red-600",
}) => {
  const normalizedCurrentPrice = Number(currentPrice || 0);
  const normalizedOriginalPrice = Number(originalPrice || 0);
  const hasOriginalPrice =
    Number.isFinite(normalizedOriginalPrice) &&
    normalizedOriginalPrice > normalizedCurrentPrice;

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`}>
      {hasOriginalPrice ? (
        <span className="inline-flex items-center rounded-full border border-rose-100 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-400 shadow-[0_10px_24px_-22px_rgba(0,0,0,0.45)] sm:text-xs">
          <span className="line-through decoration-rose-400 decoration-[1.5px]">
            ₹ {formatRupees(normalizedOriginalPrice)}
          </span>
          {/* 
          {unit ? (
            <span className="ml-1 text-[0.68em] font-semibold uppercase tracking-[0.14em] text-gray-350">
              / {unit}
            </span>
          ) : null}
          */ }
        </span>
      ) : null}

      <PriceWithUnit
        price={normalizedCurrentPrice}
        unit={unit}
        className={`whitespace-nowrap text-lg font-bold sm:text-[16px] ${highlightClassName}`}
      />
    </div>
  );
};

const getDisplayOriginalPrice = ({
  currentPrice,
  tierBasePrice,
  fallbackPrice,
}) => {
  const normalizedCurrentPrice = Number(currentPrice || 0);
  const normalizedTierBasePrice = Number(tierBasePrice || 0);
  const normalizedFallbackPrice = Number(fallbackPrice || 0);

  if (
    Number.isFinite(normalizedTierBasePrice) &&
    normalizedTierBasePrice > normalizedCurrentPrice
  ) {
    return normalizedTierBasePrice;
  }

  if (
    Number.isFinite(normalizedFallbackPrice) &&
    normalizedFallbackPrice > normalizedCurrentPrice
  ) {
    return normalizedFallbackPrice;
  }

  return 0;
};

const SavingsHighlight = ({ amount, className = "" }) => {
  const savingsAmount = Number(amount || 0);
  const gradientId = useId();

  if (!(savingsAmount > 0)) {
    return null;
  }

  return (
    <motion.div
      whileHover={{ x: 1.5 }}
      transition={{ type: "spring", stiffness: 280, damping: 20 }}
      className={`flex items-center justify-between gap-3 ${className}`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="relative flex h-7 w-7 shrink-0 items-center justify-center">
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-orange-400/30 blur-md"
            animate={{ opacity: [0.3, 0.75, 0.3], scale: [0.88, 1.18, 0.88] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.svg
            viewBox="-33 0 255 255"
            aria-hidden="true"
            className="relative h-6 w-6 drop-shadow-[0_4px_10px_rgba(251,146,60,0.45)]"
            animate={{
              y: [0, -2.2, 0],
              scale: [1, 1.05, 0.98, 1],
              rotate: [-3, 2, -1, 0],
            }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <defs>
              <linearGradient
                id={gradientId}
                gradientUnits="userSpaceOnUse"
                x1="94.141"
                y1="255"
                x2="94.141"
                y2="0.188"
              >
                <stop offset="0" stopColor="#ff4c0d" />
                <stop offset="1" stopColor="#fc9502" />
              </linearGradient>
            </defs>
            <motion.g
              animate={{
                rotate: [-2.5, 2, -1.5, 0],
                scaleY: [1, 1.05, 0.985, 1],
                x: [0, 0.8, -0.5, 0],
              }}
              transition={{ duration: 1.45, repeat: Infinity, ease: "easeInOut" }}
              style={{ originX: "50%", originY: "100%" }}
            >
              <path
                d="M187.899,164.809 C185.803,214.868 144.574,254.812 94.000,254.812 C42.085,254.812 -0.000,211.312 -0.000,160.812 C-0.000,154.062 -0.121,140.572 10.000,117.812 C16.057,104.191 19.856,95.634 22.000,87.812 C23.178,83.513 25.469,76.683 32.000,87.812 C35.851,94.374 36.000,103.812 36.000,103.812 C36.000,103.812 50.328,92.817 60.000,71.812 C74.179,41.019 62.866,22.612 59.000,9.812 C57.662,5.384 56.822,-2.574 66.000,0.812 C75.352,4.263 100.076,21.570 113.000,39.812 C131.445,65.847 138.000,90.812 138.000,90.812 C138.000,90.812 143.906,83.482 146.000,75.812 C148.365,67.151 148.400,58.573 155.999,67.813 C163.226,76.600 173.959,93.113 180.000,108.812 C190.969,137.321 187.899,164.809 187.899,164.809 Z"
                fill={`url(#${gradientId})`}
                fillRule="evenodd"
              />
            </motion.g>
            <motion.g
              animate={{
                rotate: [1.5, -2.5, 1, 0],
                scaleY: [1, 1.09, 0.95, 1],
                x: [0, -1.2, 0.8, 0],
                y: [0, -1.2, 0],
                opacity: [0.92, 1, 0.9, 0.97],
              }}
              transition={{ duration: 1.08, repeat: Infinity, ease: "easeInOut" }}
              style={{ originX: "50%", originY: "100%" }}
            >
              <path
                d="M94.000,254.812 C58.101,254.812 29.000,225.711 29.000,189.812 C29.000,168.151 37.729,155.000 55.896,137.166 C67.528,125.747 78.415,111.722 83.042,102.172 C83.953,100.292 86.026,90.495 94.019,101.966 C98.212,107.982 104.785,118.681 109.000,127.812 C116.266,143.555 118.000,158.812 118.000,158.812 C118.000,158.812 125.121,154.616 130.000,143.812 C131.573,140.330 134.753,127.148 143.643,140.328 C150.166,150.000 159.127,167.390 159.000,189.812 C159.000,225.711 129.898,254.812 94.000,254.812 Z"
                fill="#fc9502"
                fillRule="evenodd"
              />
            </motion.g>
            <motion.g
              animate={{
                rotate: [-3, 3.5, -2, 0],
                scaleY: [1, 1.12, 0.9, 1],
                x: [0, 1.6, -1.1, 0],
                y: [0, -2.4, 0],
                opacity: [0.82, 1, 0.85],
              }}
              transition={{ duration: 0.82, repeat: Infinity, ease: "easeInOut" }}
              style={{ originX: "50%", originY: "100%" }}
            >
              <path
                d="M95.000,183.812 C104.250,183.812 104.250,200.941 116.000,223.812 C123.824,239.041 112.121,254.812 95.000,254.812 C77.879,254.812 69.000,240.933 69.000,223.812 C69.000,206.692 85.750,183.812 95.000,183.812 Z"
                fill="#fce202"
                fillRule="evenodd"
              />
            </motion.g>
          </motion.svg>
        </div>

        <span className="text-sm font-medium text-gray-600">You saved</span>
      </div>

      <motion.div
        initial={{ opacity: 0.92 }}
        animate={{ opacity: [0.92, 1, 0.92] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className="text-sm font-medium text-green-600 sm:text-[0.95rem]"
      >
        ₹ {formatRupees(savingsAmount)}
      </motion.div>
    </motion.div>
  );
};

const extractPlainText = (content) => {
  if (!content) return "";

  if (typeof content === "string") {
    return content.trim();
  }

  const root =
    Array.isArray(content) ? { children: content } : content?.root || content;
  const nodes = Array.isArray(root?.children) ? root.children : [];

  const collectText = (items = []) =>
    items
      .flatMap((item) => {
        if (!item || typeof item !== "object") return [];

        if (item.type === "text" && typeof item.text === "string") {
          return [item.text];
        }

        if (Array.isArray(item.children)) {
          return collectText(item.children);
        }

        return [];
      })
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

  return collectText(nodes);
};

const getPreferredDescription = (item) =>
  extractPlainText(item?.description) ||
  (typeof item?.description === "string" && item.description.trim()) ||
  "";

const formatCartQuantitySummary = (quantity) => {
  if (!quantity) return "";

  if (typeof quantity === "string") {
    return quantity;
  }

  if (typeof quantity === "object" && !Array.isArray(quantity)) {
    return Object.entries(quantity)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => {
        const normalizedKey =
          key.toLowerCase() === "qty"
            ? "Qty"
            : key.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

        return `${normalizedKey}: ${value}`;
      })
      .join(" • ");
  }

  return String(quantity);
};

const getCartImageURL = (item) =>
  item?.image?.cloudinary?.secure_url || item?.image?.url || "";

const normalizeQtyLabel = (value, fallback = "Quantity") => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return fallback;
  }

  return value.trim().replace(/\bqty\b/gi, "Qty");
};

const getAlignedStepBounds = ({ min, max, step }) => {
  if (step <= 1) {
    return { min, max, hasAlignedRange: true };
  }

  const alignedMin = Math.ceil(min / step) * step;
  const alignedMax = Math.floor(max / step) * step;

  if (alignedMin > alignedMax) {
    return { min, max, hasAlignedRange: false };
  }

  return { min: alignedMin, max: alignedMax, hasAlignedRange: true };
};

const snapToNearestStepValue = ({ value, min, max, step }) => {
  const safeValue = Number.isFinite(value) ? value : min;
  const boundedValue = Math.max(min, Math.min(max, safeValue));

  if (step <= 1) {
    return boundedValue;
  }

  const { min: alignedMin, max: alignedMax, hasAlignedRange } = getAlignedStepBounds({
    min,
    max,
    step,
  });

  if (!hasAlignedRange) {
    return boundedValue;
  }

  const snappedValue = Math.round(boundedValue / step) * step;
  return Math.max(alignedMin, Math.min(alignedMax, snappedValue));
};

export const SocialIcon = ({ platform, followers }) => {
  const icons = {
    instagram: <FaInstagram className="text-[#C13584] text-sm" />,
    youtube: <FaYoutube className="text-[#FF0000] text-sm" />,
    facebook: <FaFacebook className="text-[#3b5998] text-sm" />,
    twitter: <FaTwitter className="text-[#1DA1F2] text-sm" />,
    linkedin: <FaLinkedin className="text-[#0e76a8] text-sm" />,
  };

  return (
    <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg">
      {icons[platform]}
      <span className="font-medium text-xs">{followers || "Followers"}</span>
    </div>
  );
};

const Card = ({
  data,
  category,
  options = false,
  id,
  className = "",
  navigateToDetails = false,
  disableHoverAnimation = false,
  copyLink = true,
  autoOpenCartModal = false,
}) => {
  const { isLoggedIn, user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [regModal, setRegModal] = useState(false);
  const [cartModal, setCartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [appliedDiscountPercentage, setAppliedDiscountPercentage] = useState(0);
  const [showPromoConfetti, setShowPromoConfetti] = useState(false);
  const [promoConfettiPieces, setPromoConfettiPieces] = useState([]);
  const [couponCode] = useState("INDORI18");
  const [inputCouponCode, setInputCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [radioPricingMode, setRadioPricingMode] = useState("detailed"); // "detailed" or "simple"
  const [transitPricingMode, setTransitPricingMode] = useState("detailed"); // "detailed" or "simple"
  const [selectedDesignOptionIndex, setSelectedDesignOptionIndex] = useState(0);
  const [designerBookingData, setDesignerBookingData] = useState(null);
  // Stable callback identity — an inline arrow here re-triggers the child's
  // onBookingSelect effect every render and loops forever.
  const handleDesignerBookingSelect = useCallback((booking) => {
    setDesignerBookingData(booking);
  }, []);
  const [marketingCartConfig, setMarketingCartConfig] = useState(null);
  const [printingDeliveryMode, setPrintingDeliveryMode] = useState("express");
  const hasAutoOpenedCartModal = useRef(false);
  const promoConfettiTimeoutRef = useRef(null);
  const shouldOpenDetails = options || navigateToDetails;
  const isDesignCatalog = isDesignCatalogCategory(category);
  const advertisingCategories = new Set([
    "radio",
    "transit",
    "cinema",
    "outdoor",
    "btl",
    "non-traditional",
    "digital",
    "newspaper",
  ]);
  const hasNumericAdPrice = [
    data?.minimum_billing_amount,
    data?.offer_rate,
    data?.price,
  ].some((value) => Number(value) > 0);
  const optionsAllOnRequest =
    Array.isArray(data?.options) &&
    data.options.length > 0 &&
    data.options.every((option) => option?.price_on_request);
  const isOnRequestPrice =
    Boolean(data?.price_on_request) ||
    Boolean(optionsAllOnRequest) ||
    (category === "outdoor" && !hasNumericAdPrice);
  const sharePath =
    category === "brand-endorsement"
      ? `/brand-endorsement/${data.slug}`
      : category === "influencer-marketing"
        ? `/influencer-marketing/${data.slug}`
        : category === "printing"
          ? `/printing/${data.slug}`
          : isDesignCatalog
            ? getDesignCatalogPath(data, category)
            : advertisingCategories.has(category)
              ? `/advertising/${category}-ads/${data.slug}`
              : null;
  const customDiscountValue = Number(
    data?.customDiscount?.discount ?? data?.customDiscount?.customDiscount ?? 0,
  );
  const resolvedDiscountValue =
    data?.discount === "custom"
      ? Number.isFinite(customDiscountValue)
        ? customDiscountValue
        : 0
      : Number(data?.discount || 0);
  const resolvedDiscountText =
    data?.discount === "custom"
      ? data?.customDiscount?.text ||
      data?.customDiscount?.customDiscountText ||
      (resolvedDiscountValue > 0 ? `${resolvedDiscountValue}% off` : "Custom offer")
      : null;
  const designPricingMode =
    data?.pricing_mode === "options" ? "options" : "direct";
  const liveDesignerSlugs = new Set([
    "live-designer-service",
    "real-time-design-service-live-designer-interaction",
  ]);
  // Booking UI only for the live-designer product (slug-gated) when admin flag is on
  const isLiveDesignerBooking =
    (Boolean(data?.liveDesignerBooking) &&
      liveDesignerSlugs.has(String(data?.slug || ""))) ||
    Boolean(data?.title?.includes("TEST_LIVE"));
  const livePrintingSlugs = new Set([
    "live-printing-service",
    "real-time-printing-service",
  ]);
  const isLivePrintingBooking =
    Boolean(data?.livePrintingBooking) ||
    livePrintingSlugs.has(String(data?.slug || "")) ||
    Boolean(data?.title?.toLowerCase().includes("live printing"));
  const designOptions = Array.isArray(data?.design_options)
    ? data.design_options.filter(Boolean)
    : [];
  const explicitDefaultDesignOptionIndex =
    isDesignCatalog && designPricingMode === "options"
      ? designOptions.findIndex((option) => Boolean(option?.is_default))
      : -1;
  const explicitDefaultDesignOption =
    explicitDefaultDesignOptionIndex >= 0
      ? {
        index: explicitDefaultDesignOptionIndex,
        option: designOptions[explicitDefaultDesignOptionIndex],
        price: Number(designOptions[explicitDefaultDesignOptionIndex]?.price || 0) || 0,
      }
      : null;
  const cheapestDesignOption =
    isDesignCatalog && designPricingMode === "options"
      ? designOptions.reduce((lowestOption, option, index) => {
        const optionPrice = Number(option?.price);

        if (!Number.isFinite(optionPrice) || optionPrice < 0) {
          return lowestOption;
        }

        if (!lowestOption || optionPrice < lowestOption.price) {
          return {
            index,
            option,
            price: optionPrice,
          };
        }

        return lowestOption;
      }, null)
      : null;
  const highestDesignOptionPrice =
    isDesignCatalog && designPricingMode === "options"
      ? designOptions.reduce((highestPrice, option) => {
        const optionPrice = Number(option?.price);

        if (!Number.isFinite(optionPrice) || optionPrice < 0) {
          return highestPrice;
        }

        return Math.max(highestPrice, optionPrice);
      }, 0)
      : 0;
  const preferredDesignOption = explicitDefaultDesignOption || cheapestDesignOption;
  const defaultDesignOptionIndex = preferredDesignOption?.index ?? 0;
  const selectedDesignOption =
    isDesignCatalog && designPricingMode === "options"
      ? designOptions[selectedDesignOptionIndex] ||
      designOptions[defaultDesignOptionIndex] ||
      designOptions[0] ||
      null
      : null;
  const baseDesignUnit =
    typeof data?.unit === "string" && data.unit.trim().length
      ? data.unit.trim()
      : "page";
  const resolvedDesignUnit =
    typeof selectedDesignOption?.unit === "string" &&
      selectedDesignOption.unit.trim().length
      ? selectedDesignOption.unit.trim()
      : baseDesignUnit;
  const resolvedDesignQuantityLabel = normalizeQtyLabel(data?.quantity_label, "Quantity");
  const baseDesignPrice =
    isDesignCatalog
      ? Number(
        designPricingMode === "options"
          ? selectedDesignOption?.price ?? 0
          : data?.price ?? 0,
      ) || 0
      : 0;
  const designCardDisplayPrice =
    isDesignCatalog && designPricingMode === "options"
      ? Number(preferredDesignOption?.price || 0)
      : Number(data?.price || 0);
  const designCardDisplayUnit =
    typeof preferredDesignOption?.option?.unit === "string" &&
      preferredDesignOption.option.unit.trim().length
      ? preferredDesignOption.option.unit.trim()
      : baseDesignUnit;
  const designQuantityPricingMode =
    data?.quantity_pricing_mode || "none";
  const designQuantityPricingRules = Array.isArray(data?.quantity_pricing_rules)
    ? data.quantity_pricing_rules
    : [];
  const explicitDefaultQuantityRule =
    isDesignCatalog &&
      (designQuantityPricingMode === "coupon" || designQuantityPricingMode === "rate")
      ? designQuantityPricingRules.find((rule) => Boolean(rule?.is_default)) || null
      : null;
  const rawResolvedDesignMinQuantity =
    isDesignCatalog
      ? Math.max(
        1,
        Number(
          selectedDesignOption?.min_quantity ??
          data?.min_quantity ??
          1,
        ) || 1,
      )
      : 1;
  const rawResolvedDesignMaxQuantity =
    isDesignCatalog
      ? Math.max(
        rawResolvedDesignMinQuantity,
        Number(
          selectedDesignOption?.max_quantity ??
          data?.max_quantity ??
          100,
        ) || 100,
      )
      : 100;
  const resolvedDesignQuantityStep =
    isDesignCatalog
      ? Math.max(
        1,
        Number(
          selectedDesignOption?.quantity_step ??
          data?.quantity_step ??
          1,
        ) || 1,
      )
      : 1;
  const {
    min: resolvedDesignMinQuantity,
    max: resolvedDesignMaxQuantity,
  } = getAlignedStepBounds({
    min: rawResolvedDesignMinQuantity,
    max: rawResolvedDesignMaxQuantity,
    step: resolvedDesignQuantityStep,
  });
  const initialDesignQuantity = snapToNearestStepValue({
    value: Number(explicitDefaultQuantityRule?.min_quantity ?? resolvedDesignMinQuantity),
    min: resolvedDesignMinQuantity,
    max: resolvedDesignMaxQuantity,
    step: resolvedDesignQuantityStep,
  });

  const getMatchingQuantityRule = (quantityValue, rules = []) => {
    if (!Array.isArray(rules) || rules.length === 0) return null;

    return (
      rules.find((rule) => {
        const minQty = Number(rule?.min_quantity ?? rule?.min_seconds ?? 0) || 0;
        const rawMax = Number(rule?.max_quantity ?? rule?.max_seconds);
        const maxQty = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : Infinity;

        return quantityValue >= minQty && quantityValue <= maxQty;
      }) || null
    );
  };

  const getFirstQuantityTierRate = (rules = [], fallbackRate = 0) => {
    if (!Array.isArray(rules) || rules.length === 0) {
      return Number(fallbackRate) || 0;
    }

    const firstRuleWithRate = [...rules]
      .filter(Boolean)
      .sort((a, b) => {
        const aMin = Number(a?.min_quantity ?? a?.min_seconds ?? 0) || 0;
        const bMin = Number(b?.min_quantity ?? b?.min_seconds ?? 0) || 0;
        return aMin - bMin;
      })
      .find((rule) => Number.isFinite(Number(rule?.rate)) && Number(rule?.rate) > 0);

    return Number(firstRuleWithRate?.rate || fallbackRate) || 0;
  };

  const getDesignOptionBounds = (option, optionIndex = -1) => {
    const min = Math.max(
      1,
      Number(option?.min_quantity ?? data?.min_quantity ?? 1) || 1,
    );
    const step = Math.max(
      1,
      Number(option?.quantity_step ?? data?.quantity_step ?? 1) || 1,
    );
    const explicitMax = Number(option?.max_quantity);
    const nextOption =
      Number.isInteger(optionIndex) && optionIndex >= 0
        ? designOptions[optionIndex + 1]
        : null;
    const nextMin = Number(nextOption?.min_quantity);
    const fallbackMax = Number(data?.max_quantity ?? 100);
    const rawMax = Number.isFinite(explicitMax) && explicitMax > 0
      ? explicitMax
      : Number.isFinite(nextMin) && nextMin > min
        ? nextMin - 1
        : fallbackMax;
    const max = Number.isFinite(rawMax) && rawMax > 0 ? Math.max(min, rawMax) : 100;

    return getAlignedStepBounds({
      min,
      max,
      step,
    });
  };

  const getMatchingDesignOptionIndex = (quantityValue) => {
    if (
      !isDesignCatalog ||
      designPricingMode !== "options" ||
      !Array.isArray(designOptions) ||
      designOptions.length === 0
    ) {
      return -1;
    }

    const normalizedQuantity = Number(quantityValue);
    if (!Number.isFinite(normalizedQuantity)) {
      return -1;
    }

    return designOptions.findIndex((option, index) => {
      const { min, max } = getDesignOptionBounds(option, index);

      return normalizedQuantity >= min && normalizedQuantity <= max;
    });
  };

  const resolveDesignQuantitySelection = (quantityValue) => {
    const matchingOptionIndex = getMatchingDesignOptionIndex(quantityValue);
    const nextOptionIndex =
      matchingOptionIndex >= 0 ? matchingOptionIndex : selectedDesignOptionIndex;
    const nextOption =
      designPricingMode === "options"
        ? designOptions[nextOptionIndex] ||
        designOptions[defaultDesignOptionIndex] ||
        selectedDesignOption
        : selectedDesignOption;

    const nextStep = Math.max(
      1,
      Number(nextOption?.quantity_step ?? data?.quantity_step ?? 1) || 1,
    );
    const { min, max } = getDesignOptionBounds(nextOption, nextOptionIndex);
    const normalizedQuantity = snapToNearestStepValue({
      value: quantityValue,
      min,
      max,
      step: nextStep,
    });

    return {
      optionIndex: nextOptionIndex,
      quantity: normalizedQuantity,
    };
  };
  const designQuantityControlBounds =
    isDesignCatalog && designPricingMode === "options" && designOptions.length > 0
      ? designOptions.reduce(
        (accumulator, option, index) => {
          const { min, max } = getDesignOptionBounds(option, index);

          return {
            min: Math.min(accumulator.min, min),
            max: Math.max(accumulator.max, max),
          };
        },
        { min: Infinity, max: 0 },
      )
      : {
        min: resolvedDesignMinQuantity,
        max: resolvedDesignMaxQuantity,
      };
  const resolvedDesignQuantityControlMin =
    Number.isFinite(designQuantityControlBounds.min)
      ? designQuantityControlBounds.min
      : resolvedDesignMinQuantity;
  const resolvedDesignQuantityControlMax =
    Number.isFinite(designQuantityControlBounds.max) && designQuantityControlBounds.max > 0
      ? designQuantityControlBounds.max
      : resolvedDesignMaxQuantity;

  const activeDesignQuantity = quantities["qty"] || initialDesignQuantity;
  const activeDesignQuantityRule =
    isDesignCatalog
      ? getMatchingQuantityRule(activeDesignQuantity, designQuantityPricingRules)
      : null;
  const applicableDesignUnitPrice =
    isDesignCatalog && designQuantityPricingMode === "rate"
      ? Number(activeDesignQuantityRule?.rate || baseDesignPrice) || baseDesignPrice
      : baseDesignPrice;
  const applicableDesignDiscountValue =
    isDesignCatalog && designQuantityPricingMode === "coupon"
      ? Number(activeDesignQuantityRule?.discount_percentage || 0) || 0
      : designQuantityPricingMode === "none"
        ? resolvedDiscountValue
        : 0;
  const applicableDesignCouponCode =
    isDesignCatalog && designQuantityPricingMode === "coupon"
      ? activeDesignQuantityRule?.coupon_code || couponCode
      : couponCode;
  const applicableDesignCouponText =
    isDesignCatalog && designQuantityPricingMode === "coupon"
      ? activeDesignQuantityRule?.coupon_label ||
      (applicableDesignDiscountValue > 0
        ? `${applicableDesignDiscountValue}% off`
        : null)
      : resolvedDiscountText;
  const hasDesignCoupon = applicableDesignDiscountValue > 0;
  const firstDesignQuantityTierRate = getFirstQuantityTierRate(
    designQuantityPricingRules,
    baseDesignPrice,
  );
  const designReferenceUnitPrice = (() => {
    const comparisonCandidates = [
      baseDesignPrice,
      Number(selectedDesignOption?.original_price),
      Number(selectedDesignOption?.compare_at_price),
      Number(selectedDesignOption?.mrp),
      Number(selectedDesignOption?.base_price),
      Number(data?.original_price),
      Number(data?.compare_at_price),
      Number(data?.mrp),
      designPricingMode === "options" ? Number(data?.price) : 0,
      designQuantityPricingMode === "rate" ? firstDesignQuantityTierRate : 0,
      highestDesignOptionPrice,
      applicableDesignUnitPrice,
    ].filter((value) => Number.isFinite(value) && value > 0);

    return comparisonCandidates.length > 0
      ? Math.max(...comparisonCandidates)
      : applicableDesignUnitPrice;
  })();
  const designDisplayOriginalUnitPrice = getDisplayOriginalPrice({
    currentPrice: applicableDesignUnitPrice,
    fallbackPrice: designReferenceUnitPrice,
  });
  const designSavingsAmount =
    isDesignCatalog && designQuantityPricingMode !== "coupon"
      ? Math.max(0, designReferenceUnitPrice - applicableDesignUnitPrice) *
      activeDesignQuantity
      : 0;
  const hasAnyDesignCoupon =
    isDesignCatalog &&
    (
      (designQuantityPricingMode === "coupon" &&
        designQuantityPricingRules.some((rule) => {
          const discountPercentage = Number(rule?.discount_percentage || 0);
          const hasCouponCode =
            typeof rule?.coupon_code === "string" &&
            rule.coupon_code.trim().length > 0;
          const hasCouponLabel =
            typeof rule?.coupon_label === "string" &&
            rule.coupon_label.trim().length > 0;

          return discountPercentage > 0 || hasCouponCode || hasCouponLabel;
        })) ||
      (designQuantityPricingMode === "none" &&
        (resolvedDiscountValue > 0 ||
          (typeof resolvedDiscountText === "string" &&
            resolvedDiscountText.trim().length > 0)))
    );
  const showDiscountBadge =
    isDesignCatalog
      ? hasAnyDesignCoupon
      : resolvedDiscountValue > 0;

  useEffect(() => {
    if (isDesignCatalog) {
      setQuantities((prev) => {
        const currentQty = Number(prev?.qty);
        const nextQty = Number.isFinite(currentQty)
          ? snapToNearestStepValue({
            value: currentQty,
            min: resolvedDesignMinQuantity,
            max: resolvedDesignMaxQuantity,
            step: resolvedDesignQuantityStep,
          })
          : initialDesignQuantity;

        if (prev?.qty === nextQty && Object.keys(prev || {}).length === 1) {
          return prev;
        }

        return { qty: nextQty };
      });
      // Do not clear coupons here — quantity snap re-runs often and was wiping applied discounts
    } else if (category === "radio") {
      // Initialize radio ad quantities
      setQuantities({
        seconds: data.min_seconds || 10,
        slots_per_day: 1,
        days: 1,
        total_seconds: data.min_seconds || 10, // For simple mode
      });
    } else if (category === "transit") {
      // Initialize transit ad quantities
      setQuantities({
        quantity: data.min_quantity || 1,
        months: 1,
        total_vehicles: data.min_quantity || 1, // For simple mode
      });
    } else if (data.price_units) {
      const initialQuantities = {};
      data.price_units.forEach((unit) => {
        initialQuantities[unit.code] = unit.minimum_value;
      });
      setQuantities(initialQuantities);
    }
  }, [
    category,
    data.price_units,
    isDesignCatalog,
    data.min_seconds,
    data.min_quantity,
    resolvedDesignMinQuantity,
    resolvedDesignMaxQuantity,
    resolvedDesignQuantityStep,
    initialDesignQuantity,
  ]);

  useEffect(() => {
    setQuantityDrafts((prev) => {
      const nextDrafts = { ...prev };

      Object.entries(quantities || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          nextDrafts[key] = String(value);
        }
      });

      return nextDrafts;
    });
  }, [quantities]);

  useEffect(() => {
    if (!isDesignCatalog) return;

    setSelectedDesignOptionIndex(defaultDesignOptionIndex);
  }, [isDesignCatalog, data?.id, data?.slug, defaultDesignOptionIndex]);

  // Reset coupon state only when the product identity changes (not on qty/option snaps)
  useEffect(() => {
    setDiscountApplied(false);
    setInputCouponCode("");
    setAppliedDiscountPercentage(0);
    setCouponError("");
  }, [data?.id, data?.slug, category]);

  useEffect(() => {
    if (!autoOpenCartModal || hasAutoOpenedCartModal.current) {
      return;
    }

    hasAutoOpenedCartModal.current = true;
    setCartModal(true);
  }, [autoOpenCartModal]);

  useEffect(() => {
    return () => {
      if (promoConfettiTimeoutRef.current) {
        window.clearTimeout(promoConfettiTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to get rate based on quantity for radio/transit ads
  const getRateForQuantity = (quantityValue, quantityBasedRates, baseRate) => {
    if (!quantityBasedRates || quantityBasedRates.length === 0) {
      return { rate: baseRate, discountPercentage: 0 };
    }

    // Find the appropriate rate tier based on quantity
    for (let rateTier of quantityBasedRates) {
      const minQty = rateTier.min_seconds || rateTier.min_quantity || 0;
      const maxQty = rateTier.max_seconds || rateTier.max_quantity || Infinity;

      if (quantityValue >= minQty && quantityValue <= maxQty) {
        return {
          rate: rateTier.rate,
          discountPercentage: rateTier.discount_percentage || 0,
        };
      }
    }

    // If no tier matches, return base rate
    return { rate: baseRate, discountPercentage: 0 };
  };

  const calculateTotalPrice = () => {
    let subtotal = 0;

    // Check if pricing_formula is defined in backend (multiplicative or additive)
    const pricingFormula = data.pricing_formula || "additive"; // Default to additive for backward compatibility

    if (
      pricingFormula === "multiplicative" ||
      category === "radio" ||
      category === "transit"
    ) {
      // Multiplicative pricing: multiply all quantities together with base rate
      // Radio: (seconds × price) × slots × days
      // Transit: (vehicles × price) × months × weeks etc.
      if (Object.keys(quantities).length > 0) {
        subtotal = data.offer_rate || 0;
        Object.entries(quantities).forEach(([unit, quantity]) => {
          subtotal *= quantity;
        });
      }
    } else {
      // Additive pricing: sum up all unit prices
      // For categories like cinema, outdoor, etc.
      Object.entries(quantities).forEach(([unit, quantity]) => {
        const priceUnit = data.price_units?.find((pu) => pu.code === unit);
        if (priceUnit) {
          subtotal += data.offer_rate * quantity;
        }
      });
    }

    const discount = discountApplied ? subtotal * (appliedDiscountPercentage / 100) : 0;
    const discountedSubtotal = subtotal - discount;
    const gst = discountedSubtotal * 0.18;

    return {
      subtotal,
      discount,
      discountedSubtotal,
      gst,
      total: discountedSubtotal + gst,
    };
  };

  const handleApplyDiscount = (e) => {
    e.stopPropagation();
    setDiscountApplied(true);
    setPromoConfettiPieces(
      createConfettiPieces(56, {
        leftRange: [8, 92],
        topRange: [2, 18],
        xRange: [-220, 220],
        yRange: [150, 320],
      }),
    );
    setShowPromoConfetti(true);

    if (promoConfettiTimeoutRef.current) {
      window.clearTimeout(promoConfettiTimeoutRef.current);
    }

    promoConfettiTimeoutRef.current = window.setTimeout(() => {
      setShowPromoConfetti(false);
    }, 2600);
  };

  const handleRemoveDiscount = () => {
    setDiscountApplied(false);
    setInputCouponCode("");
    setAppliedDiscountPercentage(0);
    setCouponError("");
  };

  const handleDesignOptionChange = (optionIndex) => {
    setSelectedDesignOptionIndex(optionIndex);
    // Keep applied coupon when switching options — discount is % of subtotal
  };

  const resolvePromoCodeLabel = (discountValue, fallbackCode = couponCode) => {
    if (discountValue === "5") return "SUMMER5";
    if (discountValue === "18") return "WELCOME18";
    if (discountValue === "50") return "BLACKFRIDAY50";
    return fallbackCode;
  };

  const getDesignOptionTitle = (option, index) =>
    option?.name || `Option ${index + 1}`;

  const getDesignOptionBadge = (option) => {
    const optionUnit =
      typeof option?.unit === "string" && option.unit.trim().length
        ? option.unit.trim()
        : baseDesignUnit;
    const optionPrice = Number(option?.price || 0) || 0;
    const isRecommended = Boolean(option?.is_recommended || option?.recommended);

    return {
      priceLabel: `₹ ${optionPrice.toLocaleString()} / ${optionUnit}`,
      highlight: isRecommended ? "Recommended" : undefined,
    };
  };

  const getDesignOptionDescription = (option) => {
    const minQty = Number(option?.min_quantity);
    const rawMax = Number(option?.max_quantity);
    const hasMinQty = Number.isFinite(minQty) && minQty > 0;
    const hasMaxQty = Number.isFinite(rawMax) && rawMax > 0;
    const quantityRangeLabel = hasMinQty
      ? hasMaxQty
        ? `Qty ${minQty}-${rawMax}`
        : `Qty ${minQty}+`
      : null;
    const baseDescription =
      typeof option?.description === "string" && option.description.trim().length
        ? option.description.trim()
        : "Select this option to update the design pricing.";

    return quantityRangeLabel
      ? `${baseDescription}`
      : baseDescription;
  };

  const getDesignOptionRangeLabel = (option) => {
    const minQty = Number(option?.min_quantity);
    const rawMax = Number(option?.max_quantity);
    const hasMinQty = Number.isFinite(minQty) && minQty > 0;
    const hasMaxQty = Number.isFinite(rawMax) && rawMax > 0;

    if (!hasMinQty) {
      return null;
    }

    return hasMaxQty ? `${minQty} to ${rawMax}` : `${minQty}+`;
  };

  const renderSelectedDesignOptionDetails = () => {
    if (!selectedDesignOption) {
      return null;
    }

    const optionDescription = getDesignOptionDescription(selectedDesignOption);
    const optionRange = getDesignOptionRangeLabel(selectedDesignOption);
    const optionUnit =
      typeof selectedDesignOption?.unit === "string" && selectedDesignOption.unit.trim().length
        ? selectedDesignOption.unit.trim()
        : resolvedDesignUnit;

    if (!optionDescription && !optionRange && !optionUnit) {
      return null;
    }

    return (
      <div className="mb-3 space-y-1 text-[11px] leading-5 text-gray-500 sm:text-[12px]">
        {/* <p className="font-semibold text-gray-700">{selectedDesignOption?.name}</p> */}
        <p>{optionDescription || "This option is active for the current quantity and pricing."}</p>
        {/*
          {(optionRange || optionUnit) ? (
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-gray-400">
              {optionRange ? `Qty ${optionRange}` : ""}
              {optionRange && optionUnit ? " • " : ""}
              {optionUnit || ""}
            </p>
          ) : null}
        */}
      </div>
    );
  };

  const renderPromoCodeCard = ({ code, description }) => {
    const handleApplyCouponInput = async (e) => {
      e?.preventDefault();
      setCouponError("");
      if (!inputCouponCode.trim()) return;

      try {
        const response = await axios.post("/api/validate-coupon", {
          code: inputCouponCode.trim(),
          collection: category,
          productId: data?.id
        });
        if (response.data?.valid) {
          setAppliedDiscountPercentage(response.data.discountPercentage || 0);
          handleApplyDiscount(e || new Event("click"));
        } else {
          setCouponError("Invalid coupon code");
          setDiscountApplied(false);
        }
      } catch (error) {
        setCouponError(error.response?.data?.error || "Error validating coupon");
        setDiscountApplied(false);
      }
    };

    return (
      <div className="space-y-1.5 mt-2">
        <ConfettiBurst
          active={showPromoConfetti}
          pieces={promoConfettiPieces}
          className="fixed inset-0 z-[9999]"
          portal
        />
        {!discountApplied ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <input
              type="text"
              value={inputCouponCode}
              onChange={(e) => setInputCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              className="w-full flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-800 outline-none transition focus:border-red-400"
              onKeyDown={(e) => e.key === "Enter" && handleApplyCouponInput(e)}
            />
            <button
              type="button"
              onClick={handleApplyCouponInput}
              className="w-full sm:w-auto rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-green-300 bg-green-50 px-3 py-2">
            <div>
              <span className="text-xs font-semibold text-green-700">{(inputCouponCode || code).toUpperCase()}</span>
              <p className="mb-0 text-[10px] text-green-600">{description || "Discount applied"}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setInputCouponCode("");
                handleRemoveDiscount();
              }}
              className="rounded-md bg-green-100 px-2.5 py-1 text-[10px] font-semibold text-green-700 transition hover:bg-green-200"
            >
              Remove
            </button>
          </div>
        )}
        {couponError && (
          <p className="text-[11px] text-red-500">{couponError}</p>
        )}
      </div>
    );
  };

  const handleAddToCart = async (
    name,
    quantities,
    category,
    discountPercentage,
  ) => {
    if (!isLoggedIn) {
      setCartModal(false);
      setRegModal(true);
      return;
    }

    if (!name) {
      toast.error("Something Went Wrong Please Refresh");
      return;
    }


    if (!user) {
      setCartModal(false);
      setRegModal(true);
      return;
    }

    try {
      setLoading(true);

      if (isLiveDesignerBooking && designerBookingData) {
        if (!designerBookingData.date) {
          toast.error("Please select a date for the live designer session");
          setLoading(false);
          return;
        }
        if (!designerBookingData.time) {
          toast.error("Please select a time for the live designer session");
          setLoading(false);
          return;
        }

        try {
          const bookingResponse = await axios.post("/api/designer-booking", {
            serviceId: data.id,
            date: designerBookingData.date,
            time: designerBookingData.time,
            duration: designerBookingData.minutes,
            serviceName: designerBookingData.service?.name || "Live Design Session",
            pricePerMinute: designerBookingData.service?.price || data.price || 9,
            subtotal: designerBookingData.subtotal || 0,
            discountAmount: designerBookingData.discount || 0,
            taxAmount: designerBookingData.tax_amount || 0,
            totalAmount: designerBookingData.total || 0,
            userId: user.id
          });
          
          const bookingResult = bookingResponse.data?.booking;

          const cartData = {
            name: `${data.title} - Live Design Session`,
            description: `Date: ${designerBookingData.date}, Time: ${designerBookingData.time}, Duration: ${designerBookingData.minutes} mins${bookingResult?.id ? ` (Booking ID: ${bookingResult.id})` : ""}`,
            image_url: getCartImageURL(data),
            quantity: {
              service: designerBookingData.service?.name || "Live Design Session",
              date: designerBookingData.date,
              time: designerBookingData.time,
              duration: designerBookingData.minutes,
            },
            subtotal: designerBookingData.subtotal || designerBookingData.total,
            discount_amount: designerBookingData.discount || 0,
            tax_amount: designerBookingData.tax_amount || 0,
            total_amount: designerBookingData.total,
          };

          await addToCart(`${data.title} - Live Design Session`, category, cartData);
          
          toast.success("Designer session added to cart! A confirmation email has been sent to you.");
          setCartModal(false);
        } catch (error) {
          toast.error(error.response?.data?.error || "Failed to book designer session");
        } finally {
          setLoading(false);
        }
        return;
      }

      // Handle printing special requirement submission
      if (category === "printing" && marketingCartConfig?.quantity?.delivery === "special_requirement") {
        const specialReqText = marketingCartConfig.quantity.special_requirement || "";
        if (!specialReqText.trim()) {
          toast.error("Please describe your special requirement");
          setLoading(false);
          return;
        }

        await axios.post("/api/printing/special-requirement", {
          userName: user?.name || "",
          userEmail: user?.email || "",
          userPhone: user?.phone || "",
          userCity: user?.city || "",
          productTitle: data.title,
          productType: data.product_type || "",
          productSlug: data.slug || "",
          specialRequirement: specialReqText,
          selectedOptions: {
            ...(marketingCartConfig.quantity.size ? { Size: marketingCartConfig.quantity.size } : {}),
            ...(marketingCartConfig.quantity.material ? { Material: marketingCartConfig.quantity.material } : {}),
            ...(marketingCartConfig.quantity.printing_side ? { "Printing Side": marketingCartConfig.quantity.printing_side } : {}),
            ...(marketingCartConfig.quantity.colour ? { Colour: marketingCartConfig.quantity.colour } : {}),
            ...(marketingCartConfig.quantity.finishing ? { Finish: marketingCartConfig.quantity.finishing } : {}),
          },
        });

        toast.success("Special requirement submitted successfully! Our team will contact you soon.");
        setLoading(false);
        setCartModal(false);
        return;
      }

      // Handle printing items with configuration
      if (category === "printing" && marketingCartConfig) {
        const subtotal = marketingCartConfig.subtotal || 0;
        const discount = marketingCartConfig.discount_amount || 0;
        const discountedSubtotal = Math.max(0, subtotal - discount);
        const taxAmount = discountedSubtotal * 0.18;
        const total = discountedSubtotal + taxAmount + (marketingCartConfig.delivery_charge || 0);
        const fullCartData = {
          ...marketingCartConfig,
          tax_amount: taxAmount,
          total_amount: total,
        };
        await addToCart(data.title, category, fullCartData);
      }
      // Handle design-like catalog items separately
      else if (isDesignCatalog) {
        const designQty = quantities["qty"] || resolvedDesignMinQuantity;
        const subtotal = applicableDesignUnitPrice * designQty;
        // Use the same percentage applied in the coupon UI so cart totals match the modal
        const discount = discountApplied
          ? subtotal * ((appliedDiscountPercentage || applicableDesignDiscountValue) / 100)
          : 0;
        const discountedSubtotal = subtotal - discount;
        const taxAmount = discountedSubtotal * 0.18;
        const cartItemName = selectedDesignOption?.name
          ? `${data.title} - ${selectedDesignOption.name}`
          : data.title;
        const cartItemDescription =
          getPreferredDescription(data) ||
          getPreferredDescription(selectedDesignOption) ||
          formatCartQuantitySummary({
            ...(selectedDesignOption?.name
              ? { option: selectedDesignOption.name }
              : {}),
            qty: designQty,
          }) ||
          "";

        const cartData = {
          name: cartItemName,
          description: cartItemDescription,
          image_url: getCartImageURL(data),
          quantity: {
            qty: designQty,
            ...(selectedDesignOption?.name
              ? {
                option: selectedDesignOption.name,
              }
              : {}),
          },
          subtotal: subtotal,
          discount_amount: discount,
          tax_amount: taxAmount,
          total_amount: discountedSubtotal + taxAmount,
        };
        await addToCart(cartItemName, category, cartData);
      } else {
        // Calculate totals based on quantities for other categories
        const totals = calculateTotalPrice();
        const cartItemDescription = getPreferredDescription(data) || "";

        // Prepare cart data according to schema
        const cartData = {
          name: data.title,
          description: cartItemDescription,
          image_url: getCartImageURL(data),
          quantity: quantities,
          subtotal: totals.subtotal,
          discount_amount: totals.discount,
          tax_amount: totals.gst,
          total_amount: totals.total,
        };
        await addToCart(name, category, cartData);
      }

      toast.success("Item added to cart successfully");
      setLoading(false);
      setCartModal(false);
      navigate("/cart");
    } catch (error) {
      console.error("Error adding item to cart:", error);
      setLoading(false);
      toast.error("Failed to add item to cart");
    }
  };

  const handleQuantityChange = (unitCode, value) => {
    if (isDesignCatalog) {
      const { optionIndex, quantity } = resolveDesignQuantitySelection(value);

      if (
        designPricingMode === "options" &&
        optionIndex >= 0 &&
        optionIndex !== selectedDesignOptionIndex
      ) {
        setSelectedDesignOptionIndex(optionIndex);
        setDiscountApplied(false);
      }

      setQuantities({ qty: quantity });
    } else if (category === "radio") {
      // For radio ads, handle seconds, slots_per_day, days, and total_seconds
      let newValue = Math.max(1, value);

      if (unitCode === "seconds" || unitCode === "total_seconds") {
        const min = data.min_seconds || 1;
        const max = data.max_seconds || 999999; // Large max for total_seconds
        newValue = Math.max(min, Math.min(max, value));
      } else if (unitCode === "slots_per_day") {
        newValue = Math.max(1, Math.min(50, value));
      } else if (unitCode === "days") {
        newValue = Math.max(1, Math.min(365, value));
      }

      setQuantities((prev) => ({
        ...prev,
        [unitCode]: newValue,
      }));
    } else if (category === "transit") {
      // For transit ads, handle quantity, months, and total_vehicles
      let newValue = Math.max(1, value);

      if (unitCode === "quantity" || unitCode === "total_vehicles") {
        const min = data.min_quantity || 1;
        const max =
          unitCode === "total_vehicles" ? 999999 : data.max_quantity || 100;
        newValue = Math.max(min, Math.min(max, value));
      } else if (unitCode === "months") {
        newValue = Math.max(1, Math.min(12, value));
      }

      setQuantities((prev) => ({
        ...prev,
        [unitCode]: newValue,
      }));
    } else {
      const unit = data.price_units?.find((u) => u.code === unitCode);
      if (!unit) return;

      // Use unit's max value or data.max_quantity, whichever is smaller
      const maxAllowed = data.max_quantity
        ? Math.min(unit.maximum_value, data.max_quantity)
        : unit.maximum_value;
      const step = unit.step || 1;
      const normalizedValue = snapToNearestStepValue({
        value,
        min: unit.minimum_value,
        max: maxAllowed,
        step,
      });

      setQuantities((prev) => ({
        ...prev,
        [unitCode]: normalizedValue,
      }));
    }
  };

  const getQuantityFieldConfig = (unitCode) => {
    if (isDesignCatalog && unitCode === "qty") {
      return {
        min: resolvedDesignQuantityControlMin,
        max: resolvedDesignQuantityControlMax,
        step: resolvedDesignQuantityStep,
        fallback: resolvedDesignQuantityControlMin,
        snap: true,
      };
    }

    if (category === "radio") {
      if (unitCode === "seconds" || unitCode === "total_seconds") {
        return {
          min: data.min_seconds || 1,
          max: data.max_seconds || 999999,
          step: 1,
          fallback: data.min_seconds || 1,
          snap: false,
        };
      }

      if (unitCode === "slots_per_day") {
        return { min: 1, max: 50, step: 1, fallback: 1, snap: false };
      }

      if (unitCode === "days") {
        return { min: 1, max: 365, step: 1, fallback: 1, snap: false };
      }
    }

    if (category === "transit") {
      if (unitCode === "quantity" || unitCode === "total_vehicles") {
        return {
          min: data.min_quantity || 1,
          max: unitCode === "total_vehicles" ? 999999 : data.max_quantity || 100,
          step: 1,
          fallback: data.min_quantity || 1,
          snap: false,
        };
      }

      if (unitCode === "months") {
        return { min: 1, max: 12, step: 1, fallback: 1, snap: false };
      }
    }

    const unit = data.price_units?.find((entry) => entry.code === unitCode);

    if (!unit) {
      return null;
    }

    const max = data.max_quantity
      ? Math.min(unit.maximum_value, data.max_quantity)
      : unit.maximum_value;

    return {
      min: unit.minimum_value,
      max,
      step: unit.step || 1,
      fallback: unit.minimum_value,
      snap: true,
    };
  };

  const handleQuantityDraftChange = (unitCode, rawValue) => {
    if (/^\d*$/.test(rawValue)) {
      setQuantityDrafts((prev) => ({
        ...prev,
        [unitCode]: rawValue,
      }));
    }
  };

  const commitQuantityDraft = (unitCode) => {
    const config = getQuantityFieldConfig(unitCode);
    if (!config) return;

    const rawValue = quantityDrafts[unitCode];
    const parsedValue = Number.parseInt(rawValue, 10);
    const fallbackValue =
      quantities[unitCode] !== undefined ? Number(quantities[unitCode]) : config.fallback;

    const nextValue = Number.isNaN(parsedValue) ? fallbackValue : parsedValue;

    handleQuantityChange(unitCode, nextValue);
    const normalizedValue =
      isDesignCatalog && unitCode === "qty"
        ? resolveDesignQuantitySelection(nextValue).quantity
        : config.snap
          ? snapToNearestStepValue({
            value: nextValue,
            min: config.min,
            max: config.max,
            step: config.step,
          })
          : Math.max(config.min, Math.min(config.max, nextValue));

    setQuantityDrafts((prev) => ({
      ...prev,
      [unitCode]: String(normalizedValue),
    }));
  };

  const getQuantityInputValue = (unitCode, fallbackValue) =>
    quantityDrafts[unitCode] ?? String(quantities[unitCode] ?? fallbackValue);

  const getUnitDisplayName = (unit) => {
    if (!unit) return "Unit";
    const resolveDisplayName = (value) => {
      if (value === "Meta Ad Pack Qty") return "Number of Pack";
      return value;
    };

    if (typeof unit.name === "string" && unit.name.trim().length) {
      return resolveDisplayName(unit.name.replace("#", ""));
    }
    if (typeof unit.code === "string" && unit.code.trim().length) {
      return resolveDisplayName(unit.code);
    }
    return "Unit";
  };

  const renderPriceUnitOptions = () => {
    if (!data.price_units || data.price_units.length === 0) return null;

    const sortedUnits = [...data.price_units].sort(
      (a, b) => a.sort_order - b.sort_order,
    );
    const totals = calculateTotalPrice();

    // Determine pricing formula
    const pricingFormula = data.pricing_formula || "additive";

    // For multiplicative pricing, identify primary unit (first unit, usually the vehicle/item)
    // and multiplier units (time-based units like month, day, week, slot)
    const timeUnits = ["month", "week", "day", "slot", "second"];
    const isPrimaryUnit = (unit, index) => {
      if (pricingFormula === "additive") return true; // All units show price for additive
      // For multiplicative, first unit OR non-time unit is primary
      return index === 0 || !timeUnits.includes(unit.code);
    };

    return (
      <div className="space-y-6">
        {sortedUnits.map((unit, index) => (
          <div key={unit.code} className="rounded-lg bg-white p-3 shadow-sm sm:p-4">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <span className="font-medium text-gray-800">
                  {getUnitDisplayName(unit)}
                </span>
              </div>
              {isPrimaryUnit(unit, index) ? (
                <PriceWithUnit
                  price={data.offer_rate}
                  unit={unit.code}
                  className="font-bold text-red-600"
                />
              ) : (
                <span className="text-sm text-gray-500">Multiplier</span>
              )}
            </div>

            <div className="flex flex-col space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto sm:flex-nowrap sm:gap-2">
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange(
                        unit.code,
                        (quantities[unit.code] || unit.minimum_value) -
                        unit.step,
                      );
                    }}
                    disabled={quantities[unit.code] <= unit.minimum_value}
                  >
                    <FaMinus className="text-sm" />
                  </button>
                  <input
                    type="text"
                    className="h-8 min-w-[72px] flex-1 rounded border py-1 text-center sm:w-[72px] sm:flex-none"
                    value={getQuantityInputValue(unit.code, unit.minimum_value)}
                    onChange={(e) => {
                      handleQuantityDraftChange(unit.code, e.target.value);
                    }}
                    onBlur={() => {
                      commitQuantityDraft(unit.code);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                    min={unit.minimum_value}
                    max={unit.maximum_value}
                    step={unit.step}
                    onClick={(e) => e.stopPropagation()}
                    inputMode="numeric"
                  />
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange(
                        unit.code,
                        (quantities[unit.code] || unit.minimum_value) +
                        unit.step,
                      );
                    }}
                    disabled={quantities[unit.code] >= unit.maximum_value}
                  >
                    <FaPlus className="text-sm" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1 text-sm text-gray-500 sm:flex-row sm:justify-between">
                <span>Min: {unit.minimum_value}</span>
                <span>Max: {unit.maximum_value}</span>
              </div>
            </div>
          </div>
        ))}

        <div className="mt-6 pt-4 border-t space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">
              ₹ {totals.subtotal.toLocaleString()}
            </span>
          </div>

          {/* Coupon/Discount Card */}
          {data.discount && data.discount !== "none" &&
            renderPromoCodeCard({
              code: resolvePromoCodeLabel(data.discount),
              description: `${appliedDiscountPercentage || data.discount || 0}% off on your order`,
            })}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discount</span>
              <span className="font-medium text-green-600">
                - ₹ {totals.discount.toLocaleString()}
              </span>
            </div>
          )}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discounted Subtotal</span>
              <span className="font-medium">
                ₹ {totals.discountedSubtotal.toLocaleString()}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-gray-600">GST (18%)</span>
            <span className="font-medium">₹ {totals.gst.toLocaleString()}</span>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-lg font-bold text-red-600">
              ₹ {totals.total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPriceForDesign = () => {
    if (designPricingMode === "options" && designOptions.length === 0) {
      return (
        <p className="text-sm text-gray-500">
          No pricing options have been configured for this item yet.
        </p>
      );
    }

    if (!baseDesignPrice && !applicableDesignUnitPrice) return null;

    const designQty = quantities["qty"] || resolvedDesignMinQuantity;
    const subtotal = applicableDesignUnitPrice * designQty;
    const discount = discountApplied
      ? subtotal * (appliedDiscountPercentage / 100)
      : 0;
    const discountedSubtotal = subtotal - discount;
    const gst = discountedSubtotal * 0.18;
    const total = discountedSubtotal + gst;

    return (
      <div className="mb-3 text-sm">
        <div className="w-full">
          {designPricingMode === "options" && designOptions.length > 0 && (
            <>
              <DesignOptionDropdown
                label="Choose Option"
                options={designOptions}
                value={selectedDesignOptionIndex}
                onChange={handleDesignOptionChange}
                getOptionTitle={getDesignOptionTitle}
                getOptionBadge={getDesignOptionBadge}
              />
              {renderSelectedDesignOptionDetails()}
            </>
          )}

          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-medium text-gray-800">
                {resolvedDesignQuantityLabel}
              </span>
              <div className="max-lg:hidden gap-1 flex items-center">
                <button
                  className="flex size-4 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:size-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("qty", designQty - resolvedDesignQuantityStep);
                  }}
                  disabled={
                    designQty - resolvedDesignQuantityStep < resolvedDesignQuantityControlMin
                  }
                >
                  <FaMinus className="text-xs" />
                </button>
                <input
                  type="text"
                  className="h-8 min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-center text-sm font-semibold text-gray-800 outline-none transition focus:border-red-400 focus:bg-white sm:w-[50px] sm:flex-none"
                  value={getQuantityInputValue("qty", designQty)}
                  onChange={(e) => {
                    handleQuantityDraftChange("qty", e.target.value);
                  }}
                  onBlur={() => {
                    commitQuantityDraft("qty");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                  min={resolvedDesignQuantityControlMin}
                  max={resolvedDesignQuantityControlMax}
                  step={resolvedDesignQuantityStep}
                  inputMode="numeric"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="flex size-4 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:size-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("qty", designQty + resolvedDesignQuantityStep);
                  }}
                  disabled={designQty >= resolvedDesignQuantityControlMax}
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
              <PriceComparison
                currentPrice={applicableDesignUnitPrice}
                originalPrice={designDisplayOriginalUnitPrice}
                unit={resolvedDesignUnit}
                className="max-w-full justify-start sm:justify-end"
              />
            </div>

            <div className="lg:hidden mt-3 flex w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1.5 shadow-sm sm:mt-4 sm:w-fit sm:rounded-lg sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none">
              <button
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:size-7"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange("qty", designQty - resolvedDesignQuantityStep);
                }}
                disabled={
                  designQty - resolvedDesignQuantityStep < resolvedDesignQuantityControlMin
                }
              >
                <FaMinus className="text-[11px]" />
              </button>
              <input
                type="text"
                className="h-9 min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-center text-sm font-semibold text-gray-800 outline-none transition focus:border-red-400 focus:bg-white sm:w-[50px] sm:flex-none"
                value={getQuantityInputValue("qty", designQty)}
                onChange={(e) => {
                  handleQuantityDraftChange("qty", e.target.value);
                }}
                onBlur={() => {
                  commitQuantityDraft("qty");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                min={resolvedDesignQuantityControlMin}
                max={resolvedDesignQuantityControlMax}
                step={resolvedDesignQuantityStep}
                inputMode="numeric"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-white transition-colors hover:bg-gray-300 disabled:opacity-50 sm:size-7"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange("qty", designQty + resolvedDesignQuantityStep);
                }}
                disabled={designQty >= resolvedDesignQuantityControlMax}
              >
                <FaPlus className="text-[11px]" />
              </button>
            </div>
          </div>

          <p className="mb-2 mt-[-1rem] text-xs text-gray-500">
            Min: {resolvedDesignQuantityControlMin} and Max: {resolvedDesignQuantityControlMax}
            {resolvedDesignQuantityStep > 1 ? ` • Step: ${resolvedDesignQuantityStep}` : ""}
          </p>

          <div className="pt-2 border-t space-y-2.5">
            <SavingsHighlight amount={designSavingsAmount} />

            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">₹ {subtotal.toLocaleString()}</span>
            </div>

            {/* Coupon/Discount Card — always available on design catalog so API-validated codes work */}
            {isDesignCatalog && (
              <div>
                <h3 className="text-sm font-semibold">Add a Promo Code</h3>
                {renderPromoCodeCard({
                  code:
                    designQuantityPricingMode === "coupon"
                      ? applicableDesignCouponCode
                      : data.discount === "custom"
                        ? applicableDesignCouponText || applicableDesignCouponCode
                        : resolvePromoCodeLabel(data.discount, applicableDesignCouponCode),
                  description:
                    applicableDesignCouponText ||
                    (appliedDiscountPercentage > 0
                      ? `${appliedDiscountPercentage}% off on your order`
                      : applicableDesignDiscountValue > 0
                        ? `${applicableDesignDiscountValue}% off on your order`
                        : "Enter a valid coupon code"),
                })}
              </div>
            )}

            {discountApplied && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Discount</span>
                <span className="font-medium text-green-600">
                  - ₹ {discount.toLocaleString()}
                </span>
              </div>
            )}

            {discountApplied && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Discounted Subtotal</span>
                <span className="font-medium">
                  ₹ {discountedSubtotal.toLocaleString()}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-gray-600">GST (18%)</span>
              <span className="font-medium">₹ {gst.toLocaleString()}</span>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-lg font-semibold">Total</span>
              <span className="text-lg font-bold text-red-600">
                ₹ {total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPriceForTransit = () => {
    if (!data.offer_rate) return null;

    let quantity, months, totalVehicles;
    let subtotal,
      applicableRate,
      quantityDiscount,
      quantitySavings,
      baseSubtotal;
    const referenceRate = getFirstQuantityTierRate(
      data.quantity_based_rates,
      data.offer_rate,
    );

    if (transitPricingMode === "simple") {
      // Simple mode: just total vehicles
      totalVehicles = quantities.total_vehicles || data.min_quantity || 1;
      const { rate, discountPercentage } = getRateForQuantity(
        totalVehicles,
        data.quantity_based_rates,
        data.offer_rate,
      );
      applicableRate = rate;
      quantityDiscount = discountPercentage;
      subtotal = applicableRate * totalVehicles;
      baseSubtotal = referenceRate * totalVehicles;
      quantitySavings = baseSubtotal - subtotal;
    } else {
      // Detailed mode: quantity × months
      quantity = quantities.quantity || data.min_quantity || 1;
      months = quantities.months || 1;
      const { rate, discountPercentage } = getRateForQuantity(
        quantity,
        data.quantity_based_rates,
        data.offer_rate,
      );
      applicableRate = rate;
      quantityDiscount = discountPercentage;
      subtotal = applicableRate * quantity * months;
      baseSubtotal = referenceRate * quantity * months;
      quantitySavings = baseSubtotal - subtotal;
    }

    const discount = discountApplied ? subtotal * (appliedDiscountPercentage / 100) : 0;
    const discountedSubtotal = subtotal - discount;
    const gst = discountedSubtotal * 0.18;
    const total = discountedSubtotal + gst;

    return (
      <div className="space-y-6">
        {/* Pricing Mode Toggle */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Pricing Mode
            </span>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTransitPricingMode("detailed");
                }}
                className={`px-3 py-1 text-xs rounded transition-colors ${transitPricingMode === "detailed"
                  ? "bg-red-600 text-white"
                  : "bg-white text-gray-600 border border-gray-300"
                  }`}
              >
                Detailed
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTransitPricingMode("simple");
                }}
                className={`px-3 py-1 text-xs rounded transition-colors ${transitPricingMode === "simple"
                  ? "bg-red-600 text-white"
                  : "bg-white text-gray-600 border border-gray-300"
                  }`}
              >
                Simple
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {transitPricingMode === "detailed"
              ? "Configure vehicles and duration"
              : "Enter total vehicles directly"}
          </p>
        </div>

        {transitPricingMode === "simple" ? (
          /* Simple Mode: Total Vehicles Input */
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <span className="font-medium text-gray-800">
                  Total Vehicles
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-bold text-red-600">
                  ₹{applicableRate?.toLocaleString()}/vehicle
                </span>
                {quantitySavings > 0 && (
                  <span className="text-xs text-green-600 font-medium">
                    {quantityDiscount}% off (was ₹
                    {data.offer_rate?.toLocaleString()})
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  className="w-8 h-8 rounded-full bg-brand-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("total_vehicles", totalVehicles - 1);
                  }}
                  disabled={totalVehicles <= (data.min_quantity || 1)}
                >
                  <FaMinus className="text-sm" />
                </button>
                <input
                  type="number"
                  className="w-24 text-center border rounded py-1"
                  value={totalVehicles}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleQuantityChange("total_vehicles", value);
                    }
                  }}
                  min={data.min_quantity || 1}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="w-8 h-8 rounded-full bg-brand-black test-white flex items-center justify-center hover:bg-gray-800 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("total_vehicles", totalVehicles + 1);
                  }}
                >
                  <FaPlus className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Detailed Mode: Quantity × Months */
          <>
            {/* Quantity Selector */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">
                    Quantity (Vehicles)
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-bold text-red-600">
                    ₹{applicableRate?.toLocaleString()}/unit
                  </span>
                  {quantitySavings > 0 && (
                    <span className="text-xs text-green-600 font-medium">
                      {quantityDiscount}% off (was ₹
                      {data.offer_rate?.toLocaleString()})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    className="w-8 h-8 rounded-full bg-brand-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange("quantity", quantity - 1);
                    }}
                    disabled={quantity <= (data.min_quantity || 1)}
                  >
                    <FaMinus className="text-sm" />
                  </button>
                  <input
                    type="number"
                    className="w-20 text-center border rounded py-1"
                    value={quantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value)) {
                        handleQuantityChange("quantity", value);
                      }
                    }}
                    min={data.min_quantity || 1}
                    max={data.max_quantity || 100}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="w-8 h-8 rounded-full bg-brand-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange("quantity", quantity + 1);
                    }}
                    disabled={quantity >= (data.max_quantity || 100)}
                  >
                    <FaPlus className="text-sm" />
                  </button>
                </div>
                <span className="text-sm text-gray-600">
                  Range: {data.min_quantity || 1}-{data.max_quantity || 100}
                </span>
              </div>
            </div>

            {/* Months Selector */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">
                    Number of Months
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("months", months - 1);
                  }}
                  disabled={months <= 1}
                >
                  <FaMinus className="text-sm" />
                </button>
                <input
                  type="number"
                  className="w-20 text-center border rounded py-1"
                  value={months}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleQuantityChange("months", value);
                    }
                  }}
                  min={1}
                  max={12}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("months", months + 1);
                  }}
                  disabled={months >= 12}
                >
                  <FaPlus className="text-sm" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Price Summary */}
        <div className="mt-6 pt-4 border-t space-y-4">
          <SavingsHighlight amount={quantitySavings} />
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">₹ {subtotal.toLocaleString()}</span>
          </div>

          {/* Coupon/Discount Card */}
          {data.discount && data.discount !== "none" &&
            renderPromoCodeCard({
              code: resolvePromoCodeLabel(data.discount),
              description: `${appliedDiscountPercentage || data.discount || 0}% off on your order`,
            })}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discount</span>
              <span className="font-medium text-green-600">
                - ₹ {discount.toLocaleString()}
              </span>
            </div>
          )}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discounted Subtotal</span>
              <span className="font-medium">
                ₹ {discountedSubtotal.toLocaleString()}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-gray-600">GST (18%)</span>
            <span className="font-medium">₹ {gst.toLocaleString()}</span>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-lg font-bold text-red-600">
              ₹ {total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPriceForRadio = () => {
    if (!data.offer_rate) return null;

    let seconds, slotsPerDay, days, totalSeconds;
    let subtotal,
      applicableRate,
      quantityDiscount,
      quantitySavings,
      baseSubtotal;
    const referenceRate = getFirstQuantityTierRate(
      data.quantity_based_rates,
      data.offer_rate,
    );

    if (radioPricingMode === "simple") {
      // Simple mode: just total seconds
      totalSeconds = quantities.total_seconds || data.min_seconds || 10;
      const { rate, discountPercentage } = getRateForQuantity(
        totalSeconds,
        data.quantity_based_rates,
        data.offer_rate,
      );
      applicableRate = rate;
      quantityDiscount = discountPercentage;
      subtotal = applicableRate * totalSeconds;
      baseSubtotal = referenceRate * totalSeconds;
      quantitySavings = baseSubtotal - subtotal;
    } else {
      // Detailed mode: seconds × slots × days
      seconds = quantities.seconds || data.min_seconds || 10;
      slotsPerDay = quantities.slots_per_day || 1;
      days = quantities.days || 1;
      const { rate, discountPercentage } = getRateForQuantity(
        seconds,
        data.quantity_based_rates,
        data.offer_rate,
      );
      applicableRate = rate;
      quantityDiscount = discountPercentage;
      subtotal = applicableRate * seconds * slotsPerDay * days;
      baseSubtotal = referenceRate * seconds * slotsPerDay * days;
      quantitySavings = baseSubtotal - subtotal;
    }

    const discount = discountApplied ? subtotal * (appliedDiscountPercentage / 100) : 0;
    const discountedSubtotal = subtotal - discount;
    const gst = discountedSubtotal * 0.18;
    const total = discountedSubtotal + gst;

    return (
      <div className="space-y-6">
        {/* Pricing Mode Toggle */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Pricing Mode
            </span>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRadioPricingMode("detailed");
                }}
                className={`px-3 py-1 text-xs rounded transition-colors ${radioPricingMode === "detailed"
                  ? "bg-red-600 text-white"
                  : "bg-white text-gray-600 border border-gray-300"
                  }`}
              >
                Detailed
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRadioPricingMode("simple");
                }}
                className={`px-3 py-1 text-xs rounded transition-colors ${radioPricingMode === "simple"
                  ? "bg-red-600 text-white"
                  : "bg-white text-gray-600 border border-gray-300"
                  }`}
              >
                Simple
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {radioPricingMode === "detailed"
              ? "Configure seconds, slots per day, and duration"
              : "Enter total seconds directly"}
          </p>
        </div>

        {radioPricingMode === "simple" ? (
          /* Simple Mode: Total Seconds Input */
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <span className="font-medium text-gray-800">Total Seconds</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-bold text-red-600">
                  ₹{applicableRate?.toLocaleString()}/sec
                </span>
                {quantitySavings > 0 && (
                  <span className="text-xs text-green-600 font-medium">
                    {quantityDiscount}% off (was ₹
                    {data.offer_rate?.toLocaleString()})
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("total_seconds", totalSeconds - 1);
                  }}
                  disabled={totalSeconds <= (data.min_seconds || 1)}
                >
                  <FaMinus className="text-sm" />
                </button>
                <input
                  type="number"
                  className="w-24 text-center border rounded py-1"
                  value={totalSeconds}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleQuantityChange("total_seconds", value);
                    }
                  }}
                  min={data.min_seconds || 1}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("total_seconds", totalSeconds + 1);
                  }}
                >
                  <FaPlus className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Detailed Mode: Seconds × Slots × Days */
          <>
            {/* Seconds Selector */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">
                    Ad Duration (Seconds)
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-bold text-red-600">
                    ₹{applicableRate?.toLocaleString()}/sec
                  </span>
                  {quantitySavings > 0 && (
                    <span className="text-xs text-green-600 font-medium">
                      {quantityDiscount}% off (was ₹
                      {data.offer_rate?.toLocaleString()})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange("seconds", seconds - 1);
                    }}
                    disabled={seconds <= (data.min_seconds || 1)}
                  >
                    <FaMinus className="text-sm" />
                  </button>
                  <input
                    type="number"
                    className="w-20 text-center border rounded py-1"
                    value={seconds}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value)) {
                        handleQuantityChange("seconds", value);
                      }
                    }}
                    min={data.min_seconds || 1}
                    max={data.max_seconds || 120}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuantityChange("seconds", seconds + 1);
                    }}
                    disabled={seconds >= (data.max_seconds || 120)}
                  >
                    <FaPlus className="text-sm" />
                  </button>
                </div>
                <span className="text-sm text-gray-600">
                  Range: {data.min_seconds || 10}-{data.max_seconds || 60}s
                </span>
              </div>
            </div>

            {/* Slots Per Day Selector */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">
                    Slots per Day
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("slots_per_day", slotsPerDay - 1);
                  }}
                  disabled={slotsPerDay <= 1}
                >
                  <FaMinus className="text-sm" />
                </button>
                <input
                  type="number"
                  className="w-20 text-center border rounded py-1"
                  value={slotsPerDay}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleQuantityChange("slots_per_day", value);
                    }
                  }}
                  min={1}
                  max={50}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("slots_per_day", slotsPerDay + 1);
                  }}
                  disabled={slotsPerDay >= 50}
                >
                  <FaPlus className="text-sm" />
                </button>
              </div>
            </div>

            {/* Number of Days Selector */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">
                    Number of Days
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("days", days - 1);
                  }}
                  disabled={days <= 1}
                >
                  <FaMinus className="text-sm" />
                </button>
                <input
                  type="number"
                  className="w-20 text-center border rounded py-1"
                  value={days}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleQuantityChange("days", value);
                    }
                  }}
                  min={1}
                  max={365}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuantityChange("days", days + 1);
                  }}
                  disabled={days >= 365}
                >
                  <FaPlus className="text-sm" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Price Summary */}
        <div className="mt-6 pt-4 border-t space-y-4">
          <SavingsHighlight amount={quantitySavings} />
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">₹ {subtotal.toLocaleString()}</span>
          </div>

          {/* Coupon/Discount Card */}
          {data.discount && data.discount !== "none" &&
            renderPromoCodeCard({
              code: resolvePromoCodeLabel(data.discount),
              description: `${appliedDiscountPercentage || data.discount || 0}% off on your order`,
            })}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discount</span>
              <span className="font-medium text-green-600">
                - ₹ {discount.toLocaleString()}
              </span>
            </div>
          )}

          {discountApplied && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Discounted Subtotal</span>
              <span className="font-medium">
                ₹ {discountedSubtotal.toLocaleString()}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-gray-600">GST (18%)</span>
            <span className="font-medium">₹ {gst.toLocaleString()}</span>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-lg font-bold text-red-600">
              ₹ {total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderCategorySpecificDetails = () => {
    switch (category) {
      case "radio":
        return (
          <div className="grid grid-cols-2 gap-x-2 gap-y-2">
            {data.station && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaRadio className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatFieldValue(data.station)}
                </span>
              </div>
            )}
            {data.language && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaLanguage className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatFieldValue(data.language)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.audience && data.audience.length > 0 && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaUsers className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatAudience(data.audience)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatFieldValue(
                    data.locations[0].city || data.locations[0].state,
                  )}
                </span>
              </div>
            )}
          </div>
        );

      case "cinema":
        return (
          <>
            {data.theater && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaFilm className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.theater)}
                </span>
              </div>
            )}
            {data.screen_type && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <span className="text-[#bc1535] font-semibold flex-shrink-0">
                  Screen:
                </span>
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.screen_type)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </>
        );

      case "transit":
        return (
          <div className="grid grid-cols-2 gap-x-2 gap-y-2">
            {(data.mediaType || data.vehicle_type) && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaBus className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatMediaType(data.mediaType || data.vehicle_type)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-1 text-sm text-gray-600 col-span-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0 text-xs" />
                <span className="font-medium text-gray-800 text-xs truncate">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
        );

      case "outdoor":
        return (
          <>
            {data.type && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaBullhorn className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.type)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {(data.size?.width || data.size?.height) && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.size?.width} x {data.size?.height}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
            {data.location_url && (
              <a
                href={data.location_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 text-sm text-gray-600 mb-2 hover:text-red-600"
              >
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-red-600 underline">
                  View on Google Maps
                </span>
              </a>
            )}
          </>
        );

      case "non-traditional":
        return (
          <>
            {data.class && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaBullhorn className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.class)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </>
        );

      case "btl":
        return (
          <>
            {data.activity_type && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaBullhorn className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.activity_type)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </>
        );

      case "digital":
        return (
          <>
            {data.platform && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaBullhorn className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatFieldValue(data.platform)}
                </span>
              </div>
            )}
            {data.tier && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaLayerGroup className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {formatTierName(data.tier)}
                </span>
              </div>
            )}
            {data.locations && data.locations.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <FaMapMarkedAlt className="text-[#bc1535] flex-shrink-0" />
                <span className="font-medium text-gray-800">
                  {data.locations
                    .filter((loc) => loc != null)
                    .map((loc) =>
                      formatFieldValue(loc.city || loc.state || loc.country),
                    )
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </>
        );

      // case "influencer-marketing":
      //   return (
      //     <>
      //       <div className="flex items-center mb-2 text-sm">
      //         <span className="text-gray-600">Platform</span>
      //         <span className="mx-2">:</span>
      //         <span className="font-medium capitalize">
      //           {data.platforms?.join(", ")}
      //         </span>
      //       </div>
      //       <div className="flex items-center mb-2 text-sm">
      //         <span className="text-gray-600">Tier</span>
      //         <span className="mx-2">:</span>
      //         <span className="font-medium capitalize">{data.tier}</span>
      //       </div>
      //       {data.categories && data.categories.length > 0 && (
      //         <div className="flex items-center mb-2 text-sm">
      //           <span className="text-gray-600">Categories</span>
      //           <span className="mx-2">:</span>
      //           <span className="font-medium">
      //             {data.categories.join(", ")}
      //           </span>
      //         </div>
      //       )}
      //       {data.followers && (
      //         <div className="flex items-center mb-2 text-sm">
      //           <span className="text-gray-600">Followers</span>
      //           <span className="mx-2">:</span>
      //           <span className="font-medium">
      //             {data.followers.toLocaleString()}
      //           </span>
      //         </div>
      //       )}
      //     </>
      //   );

      case "brand-endorsement":
      case "influencer-marketing":
        return (
          <>
            <div className="flex flex-col space-y-2">
              {data.verified_id && (
                <a
                  href={data?.verified_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                >
                  {data.verified_id}
                </a>
              )}
              <div className="flex flex-wrap items-center">
                <span className="text-gray-600">Platforms:</span>
                <div className="flex flex-wrap gap-2">
                  {data?.socials
                    ?.filter((social) => social != null)
                    .map((social, index) => (
                      <SocialIcon
                        key={index}
                        platform={social.platform}
                        followers={social.followers}
                      />
                    ))}
                </div>
              </div>
              <div className="flex items-center mt-2">
                <span className="text-gray-600">Category</span>
                <span className="mx-2">:</span>
                <span className="font-medium capitalize">{data.category}</span>
              </div>
              <div className="flex items-center mt-2">
                <span className="text-gray-600">Copy Link</span>
                <span
                  onClick={handleCopyLink}
                  className="ml-2 text-black hover:text-blue-700 cursor-pointer"
                >
                  <FaRegCopy />
                </span>
              </div>
            </div>
          </>
        );

      case "newspaper":
        return (
          <>
            <div className="flex items-center mb-2 text-sm">
              <span className="text-gray-600">Readers</span>
              <span className="mx-2">:</span>
              <span className="font-medium capitalize">
                {data.total_readers.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center mb-2 text-sm">
              <span className="text-gray-600">Language</span>
              <span className="mx-2">:</span>
              <span className="font-medium capitalize">{data.language}</span>
            </div>
            <div className="flex items-center mb-2 text-sm">
              <span className="text-gray-600">Coverage</span>
              <span className="mx-2">:</span>
              <span className="font-medium capitalize">{data.coverage}</span>
            </div>
          </>
        );

      case "designing":
      case "printing":
        return (
          <>
            {(data.category || data.type) && (
              <div className="flex items-center mb-2 text-sm">
                <span className="text-gray-600">Category</span>
                <span className="mx-2">:</span>
                <span className="font-medium text-brand-black capitalize">
                  {data.category || data.type}
                </span>
              </div>
            )}
            {data.format && (
              <div className="flex items-start mb-2 text-sm">
                <span className="text-gray-600 shrink-0">File Format</span>
                <span className="mx-2">:</span>
                <span className="font-medium text-brand-black">
                  {data.format}
                </span>
              </div>
            )}
            {data.brochure && (data.brochure.url || data.brochure.cloudinary?.secure_url) && (
              <div className="flex items-center mb-2 text-sm">
                <span className="text-gray-600">Brochure</span>
                <span className="mx-2">:</span>
                <span className="font-medium text-brand-black">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoggedIn) {
                        setRegModal(true);
                        return;
                      }
                      const url = data.brochure.cloudinary?.secure_url || data.brochure.url;
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `${data.title || "brochure"}.pdf`;
                      link.target = "_blank";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="flex items-center gap-1.5 rounded bg-[#ED1C24] px-2.5 py-1 text-xs text-white hover:bg-red-700 transition-colors"
                  >
                    <FaDownload className="text-xs" />
                    Download
                  </button>
                </span>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const handleCardClick = () => {
    if (shouldOpenDetails) {
      if (category === "brand-endorsement") {
        // navigate(`/brand-endorsement-detail?slug=${data.slug}`);
      } else if (category === "influencer-marketing") {
        // navigate(`/influencer-detail?slug=${data.slug}`);
      } else if (category === "newspaper") {
        navigate(`/advertising/newspaper-ads/${data.slug}`, { state: { data } });
      } else if (isDesignCatalog) {
        navigate(getDesignCatalogPath(data, category), { state: { data } });
      } else if (advertisingCategories.has(category)) {
        navigate(`/advertising/${category}-ads/${data.slug}`, { state: { data } });
      } else {
        navigate(`/${category}-advertising/${data.slug}`, { state: { data } });
      }
    } else if (!isLoggedIn) {
      setRegModal(true);
    } else {
      setCartModal(true);
    }
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();

    console.log("Share Path:: ", sharePath);
    console.log("category:: ", category);

    if (!sharePath) return;

    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${sharePath}`,
      );
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <>
      <div>
        <div
          className={`border shadow-sm bg-white rounded-lg cursor-pointer relative overflow-hidden ${disableHoverAnimation
            ? ""
            : "hover:shadow-md hover:scale-105 transition-all duration-300"
            } scroll-mt-28 md:scroll-mt-36 ${className}`.trim()}
          id={id || `design-${data.title?.split(" ")[0]?.toLowerCase()}`}
          onClick={handleCardClick}
          style={{ minHeight: isLoggedIn ? "345px" : "340px" }}
        >
          {showDiscountBadge && (
            <div
              className="absolute right-0 top-0 py-1.5 px-3"
              style={{
                "--f": "6px" /* folded part */,
                "--r": "10px" /* ribbon shape */,
                "--t": "0px" /* top offset */,
                background: "var(--brand-red, #bc1535)",
                clipPath: `polygon(
                  0 0,
                  100% 0,
                  100% calc(100% - var(--f)),
                  calc(100% - var(--f)) 100%,
                  calc(100% - var(--f)) calc(100% - var(--f)),
                  0 calc(100% - var(--f)),
                  var(--r) calc(50% - var(--f)/2)
                )`,
                boxShadow: "0 calc(-1*var(--f)) 0 inset rgba(0,0,0,0.15)",
                zIndex: 10,
              }}
            >
              <p className="mb-1 text-[11px] font-bold text-white sm:text-xs">
                Discount Available
              </p>
            </div>
          )}
          <div className="relative overflow-hidden rounded-t p-2">
            {data?.audio && data?.audio?.url ? (
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full h-36 object-cover rounded-md"
              >
                <AudioPlayer audioUrl={data.audio.url} />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={data?.image?.cloudinary ? data.image.cloudinary.secure_url : data?.image?.url ?? "https://placehold.co/600x400"}
                  alt={data.title}
                  className="h-36 w-full rounded-md object-cover object-center"
                />
                {(category === "radio" || category === "transit"
                  ? data.youtube_video_card || data.youtube_video
                  : data.youtube || data.youtube_video) && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      className="absolute bottom-2 right-2 z-20"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsYoutubeModalOpen(true);
                      }}
                    >
                      <motion.button
                        type="button"
                        aria-label="Play YouTube video"
                        className="bg-white rounded-full p-1"
                        animate={{
                          boxShadow: [
                            "0 0 0 0 rgba(212, 26, 63, 0.45)",
                            "0 0 0 8px rgba(212, 26, 63, 0.15)",
                            "0 0 0 0 rgba(212, 26, 63, 0.45)",
                          ],
                          scale: [1, 1.06, 1],
                        }}
                        transition={{
                          duration: 1.6,
                          ease: "easeInOut",
                          repeat: Infinity,
                        }}
                      >
                        <SiYoutubemusic className="text-3xl text-[#d41a3f]" />
                      </motion.button>
                    </motion.div>
                  )}
              </div>
            )}
            {/* {data.hasChildren && (
              <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm">
                {data.options?.length || 0} options
              </div>
            )} */}
          </div>
          <div className="flex h-[56%] flex-col justify-between p-3 sm:p-4">
            <div className="flex items-center">
              {(isLiveDesignerBooking || isLivePrintingBooking) && (
                <div
                  className="mr-2 relative flex items-center"
                  title={isLiveDesignerBooking ? "Live Designer Booking Available" : "Live Printing Available"}
                >
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600"></span>
                  </span>
                </div>
              )}
              <h6 className="line-clamp-2 text-base font-semibold leading-snug sm:text-[17px] md:text-lg">
                {category === "newspaper"
                  ? data.publication_name
                  : category === "brand-endorsement" ||
                    category === "influencer-marketing"
                    ? data.name
                    : data.title}
              </h6>
              {(category === "influencer-marketing" ||
                category === "brand-endorsement") &&
                data.verified && (
                  <div className="ml-2 -mt-2">
                    <svg
                      aria-label="Verified"
                      class="x1lliihq x1n2onr6"
                      fill="rgb(0, 149, 246)"
                      height="18"
                      role="img"
                      viewBox="0 0 40 40"
                      width="18"
                    >
                      <title>Verified</title>
                      <path
                        d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z"
                        fill-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                )}
            </div>
            {(data?.star_rating?.show_rating || (copyLink && sharePath)) && (
              <div className="flex flex-wrap items-center gap-2 mt-1.5 mb-1">
                {data?.star_rating?.show_rating && (
                  <div className="flex items-center gap-1">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => {
                        const ratingValue = data?.star_rating?.rating_value || 4.8;
                        return (
                          <svg
                            key={i}
                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${i < Math.floor(ratingValue) ? "text-[#bc1535]" : "text-gray-300"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        );
                      })}
                    </div>
                    <span className="text-[11px] sm:text-xs font-medium text-gray-700 ml-1">
                      {data?.star_rating?.rating_value || 4.8}{" "}
                      <span className="text-gray-500 font-normal">
                        ({data?.star_rating?.reviews_count || "120+ reviews"})
                      </span>
                    </span>
                  </div>
                )}
                {copyLink && sharePath && (
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 transition hover:border-[#bc1535] hover:text-[#bc1535] sm:text-xs"
                    aria-label="Copy product link"
                  >
                    <FaRegCopy className="text-sm" />
                    <span>Copy Link</span>
                  </button>
                )}
              </div>
            )}
            {renderCategorySpecificDetails()}
            {category === "influencer-marketing" && data.show_price === true && Number(data.price) > 0 && (
              <p className="mb-3 mt-3 flex items-center gap-2 text-sm">
                <span className="text-gray-600">Price:</span>
                <PriceWithUnit price={data.price} className="text-lg font-bold text-brand-red" />
              </p>
            )}
            {category === "brand-endorsement" ||
              category === "influencer-marketing" ? null : (
              <>
                {isOnRequestPrice && (
                  <p className="mb-3 flex items-center text-xs sm:text-sm">
                    <span className="text-gray-600">Price</span>
                    <span className="mx-1.5 sm:mx-2">:</span>
                    <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                      On Request
                    </span>
                  </p>
                )}
                {isLoggedIn &&
                  !isOnRequestPrice &&
                  !data.offer_rate &&
                  data.minimum_billing_amount &&
                  !data.hasChildren && (
                    <p className="mb-3 flex items-center text-xs sm:text-sm">
                      <span className="text-gray-600">Price</span>
                      <span className="mx-1.5 sm:mx-2">:</span>
                      <PriceWithUnit
                        price={data.minimum_billing_amount}
                        unit={data.unit}
                        className="text-lg font-bold text-brand-red sm:text-xl"
                      />
                    </p>
                  )}
                {isLoggedIn && !isOnRequestPrice && data.offer_rate && !data.hasChildren && (
                  <p className="mb-3 flex items-center text-xs sm:text-sm">
                    <span className="text-gray-600">Price</span>
                    <span className="mx-1.5 sm:mx-2">:</span>
                    <PriceWithUnit
                      price={data.offer_rate}
                      unit={data.unit}
                      className="text-lg font-bold text-brand-red sm:text-xl"
                    />
                  </p>
                )}
                {isLoggedIn && isDesignCatalog && !!designCardDisplayPrice && (
                  <p className="mb-3 flex items-center text-xs sm:text-sm">
                    <span className="text-gray-600">Price</span>
                    <span className="mx-1.5 sm:mx-2">:</span>
                    <PriceWithUnit
                      price={designCardDisplayPrice}
                      unit={designCardDisplayUnit}
                      className="text-lg font-bold text-brand-red sm:text-xl"
                    />
                  </p>
                )}
              </>
            )}
            {category === "newspaper" ? (
              <button className="w-full rounded bg-brand-black py-2 text-sm text-white transition-colors hover:bg-brand-black/80 sm:text-base">
                View Details
              </button>
            ) : category === "brand-endorsement" ||
              category === "influencer-marketing" ||
              isOnRequestPrice ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowQuotationModal(true);
                }}
                className="mt-4 w-full rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 sm:text-base"
              >
                Get Quotation
              </button>
            ) : (
              <>
                {shouldOpenDetails || data.options?.length > 0 ? (
                  <button className="w-full rounded bg-brand-black py-2 text-sm text-white transition-colors hover:bg-brand-black/80 sm:text-base">
                    View Details
                  </button>
                ) : isLoggedIn ? (
                  <button
                    className="w-full rounded bg-brand-black py-2 text-sm text-white transition-colors hover:bg-brand-black/80 sm:text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCartModal(true);
                    }}
                  >
                    Add to Cart
                  </button>
                ) : (
                  <button
                    className="w-full rounded bg-brand-black py-2 text-sm text-white transition-colors hover:bg-brand-black/80 sm:text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRegModal(true);
                    }}
                  >
                    View Price
                  </button>
                )}
              </>
            )}
          </div>
          <div className="w-full h-8 rounded-b-lg" />
        </div>
      </div>

      <YoutubeModal
        isOpen={isYoutubeModalOpen}
        onClose={() => setIsYoutubeModalOpen(false)}
        videoId={getYoutubeVideoId(
          category === "radio" || category === "transit"
            ? data?.youtube_video_card || data?.youtube_video
            : data?.youtube || data?.youtube_video,
        )}
      />

      <BrandEndorsementQuotationModal
        brandEndorsementName={data?.title || data?.name || data?.publication_name}
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        type={
          category === "brand-endorsement"
            ? "brand"
            : category === "influencer-marketing"
              ? "influencer"
              : category
        }
      />

      {/* Cart Modal */}
      <Modal
        isOpen={cartModal}
        toggle={() => setCartModal(!cartModal)}
        className="modal-dialog-centered mx-[0.25rem] my-2 sm:mx-auto sm:my-6"
        style={{
          width: `min(calc(100vw - 0.5rem), ${category === "printing" ? "560px" : "460px"})`,
          maxWidth: category === "printing" ? "560px" : "460px",
          margin: "0.25rem auto",
        }}
        size="md"
      >
        {/* Flex column bounded by viewport: header fixed, middle scrolls, footer (Book Now) pinned */}
        <div className="relative flex max-h-[calc(100dvh-1.5rem)] w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          {/* Vertical Custom Requirement Button attached directly to outer Modal border */}
          {category === "printing" && printingDeliveryMode !== "special_requirement" && (
            <button
              type="button"
              onClick={() => setPrintingDeliveryMode("special_requirement")}
              className="absolute right-0 bottom-[12%] z-50 flex items-center justify-center rounded-l-lg px-1.5 py-4 transition-all duration-200 shadow-md bg-neutral-800 text-white hover:bg-neutral-700"
              style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
            >
              <span className="text-[10px] font-bold tracking-widest uppercase rotate-180">
                Custom Requirement
              </span>
            </button>
          )}

          {/* Modal Header */}
          <div className="flex shrink-0 items-start justify-between gap-3 rounded-t-lg border-b bg-gray-50 px-4 py-3 sm:px-6 sm:py-4">
            <h4 className="line-clamp-2 text-base font-bold text-gray-800 sm:text-xl">{data.title}</h4>
            <button
              aria-label="Close"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black text-white shadow-md transition-transform hover:scale-105"
              onClick={() => setCartModal(false)}
            >
              <LuX className="text-lg sm:text-xl" />
            </button>
          </div>

          {/* Scrollable Middle */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2 pt-2.5 sm:px-6 sm:pt-3">
            <div className="relative mb-2 rounded-lg bg-gray-100 p-2.5 sm:mb-3 sm:p-4">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 -translate-y-1/2"
                style={cartModalCutoutStyle}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-1/2 rotate-180"
                style={cartModalCutoutStyle}
              />
              <div className="z-10">
                {isLiveDesignerBooking ? (
                  <LiveDesignerBooking
                    serviceId={data?.id}
                    designService={data}
                    userId={user?.id}
                    basePrice={Number(data?.price) || 9}
                    onBookingSelect={handleDesignerBookingSelect}
                  />
                ) : category === "printing" ? (
                  <div className="w-full">
                    <PrintingCart
                      data={data}
                      isLoggedIn={isLoggedIn}
                      externalDeliveryMode={printingDeliveryMode}
                      onDeliveryModeChange={setPrintingDeliveryMode}
                      onLoginRequired={() => {
                        setCartModal(false);
                        setRegModal(true);
                      }}
                      onAddToCart={(cartData) => {
                        setMarketingCartConfig(cartData);
                      }}
                    />
                  </div>
                ) : isDesignCatalog ? (
                  <div className="w-full">{renderPriceForDesign()}</div>
                ) : category === "radio" ? (
                  <div className="w-full">{renderPriceForRadio()}</div>
                ) : category === "transit" ? (
                  <div className="w-full">{renderPriceForTransit()}</div>
                ) : (
                  renderPriceUnitOptions()
                )}
              </div>
            </div>
          </div>

          {/* Sticky Footer — action buttons stay visible on mobile */}
          <div className="shrink-0 border-t border-gray-100 bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5 sm:px-6 sm:pb-4">
            <div className="flex gap-4">
              <button
                className={`flex-1 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center gap-2 ${
                  marketingCartConfig?.quantity?.delivery === "special_requirement"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-brand-black text-white hover:bg-gray-800"
                }`}
                onClick={() =>
                  handleAddToCart(data.title, quantities, category)
                }
                disabled={
                  loading ||
                  (isLiveDesignerBooking &&
                    (!designerBookingData?.date || !designerBookingData?.time))
                }
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : isLiveDesignerBooking ? (
                  "Book Now"
                ) : marketingCartConfig?.quantity?.delivery === "special_requirement" ? (
                  "Submit"
                ) : (
                  <>
                    <FaCartShopping className="text-base" />
                    <span>Order Now</span>
                  </>
                )}
              </button>
            </div>
            <div className="mt-2 flex justify-center items-center gap-1.5 text-[11px] sm:text-xs text-gray-500 font-medium">
              <FaLock className="text-gray-400" />
              <span>Secure Payment <span className="text-gray-400 mx-0.5">•</span> 100% Safe & Trusted</span>
            </div>
          </div>
        </div>
      </Modal>

      <RegisterModal isOpen={regModal} toggle={() => setRegModal(!regModal)} />
    </>
  );
};

export default Card;
